// Real-world alias / synonym expansion for JVVNL search. Maps the many ways a
// user might *type* a concept (abbreviations, misspellings, industry jargon,
// mental models, alternate spellings, singular/plural) onto the canonical words
// that actually appear in entity / metric / page labels.
//
// Aliases rank BELOW exact/prefix/contains matches but ABOVE fuzzy (their own
// tier in searchUtils). Expansion is cached so the per-keystroke cost stays flat.

export const SEARCH_ALIASES: Record<string, string[]> = {
  feeder: [
    "feeder", "feedr", "feadr", "fdr", "line", "outgoing", "outgoing feeder",
    "distribution feeder", "power line", "load feeder",
  ],
  substation: [
    "substation", "sub station", "sub-station", "sstn", "ss", "station", "grid",
    "grid station", "gss", "receiving station", "power station",
  ],
  transco: [
    "transco", "transmission", "transmission station", "transmission substation",
    "transmission ss", "transmission grid", "gss", "grid", "132kv", "220kv", "400kv",
  ],
  discom: [
    "discom", "distribution", "distribution station", "distribution substation",
    "distribution ss", "dss", "33kv", "11kv", "local substation",
  ],
  circle: [
    "circle", "zone", "region", "area", "district", "division", "cluster",
    "operational area",
  ],
  plant: [
    "plant", "generator", "generation", "generation unit", "generation station",
    "power plant", "source", "producer",
  ],
  solar: [
    "solar", "pv", "solar plant", "solar farm", "photovoltaic", "solar generation",
    "solar station", "sun", "sunlight",
  ],
  wind: [
    "wind", "wind plant", "wind generation", "windmill", "wind turbine", "turbine",
    "wind station",
  ],
  thermal: [
    "thermal", "coal", "coal plant", "coal generation", "thermal generation",
    "thermal station", "thermal unit",
  ],
  load: [
    "load", "demand", "consumption", "usage", "drawal", "power demand",
    "electricity demand", "peak demand",
  ],
  peak_load: [
    "peak load", "peak demand", "max demand", "maximum demand", "highest load",
    "peak usage", "md",
  ],
  generation: [
    "generation", "gen", "produce", "production", "power generation",
    "energy generation", "output",
  ],
  net_load: [
    "net load", "effective load", "actual load", "remaining load", "residual load",
    "net demand",
  ],
  net_consumption: [
    "net consumption", "consumption", "energy usage", "electricity usage",
    "power usage", "energy consumed",
  ],
  forecast: [
    "forecast", "forcast", "prediction", "predict", "predicted", "expected",
    "estimate", "estimated", "projection", "projected", "future",
  ],
  weather: [
    "weather", "climate", "meteorology", "environment", "weather data",
    "weather condition",
  ],
  temperature: [
    "temperature", "temp", "heat", "thermal", "degree", "degrees", "hot", "cold",
  ],
  humidity: [
    "humidity", "humid", "moisture", "rh", "relative humidity", "wetness",
  ],
  rain: [
    "rain", "rainfall", "precipitation", "precip", "showers", "drizzle",
  ],
  wind_weather: [
    "wind", "wind speed", "airflow", "air flow", "gust", "breeze", "wind velocity",
  ],
  cloud: [
    "cloud", "clouds", "cloud cover", "overcast", "sky cover",
  ],
  sunshine: [
    "sun", "sunshine", "sunlight", "solar exposure", "daylight",
  ],
  radiation: [
    "radiation", "solar radiation", "irradiance", "ghi", "dni", "solar intensity",
  ],
  forecast_adjustment: [
    "forecast adjustment", "adjustment", "correction", "forecast correction",
    "manual adjustment", "override",
  ],
  load_map: [
    "load map", "map", "network map", "grid map", "feeder map", "topology",
    "geographic view",
  ],
  data_quality: [
    "data quality", "quality", "validation", "missing data", "bad data",
    "data issue", "anomaly", "accuracy", "health",
  ],
  ranking: [
    "ranking", "rank", "top feeders", "bottom feeders", "highest load",
    "lowest load", "top consumers", "best feeders", "worst feeders", "leaderboard",
  ],
};

/**
 * The canonical words a category should *match against* item text. Picked to be
 * the words that actually appear in JVVNL labels for that family, so expanding
 * an alias surfaces the right pages/metrics/entities. Keyed by SEARCH_ALIASES key.
 */
const CANONICAL_TERMS: Record<string, string[]> = {
  feeder: ["feeder"],
  substation: ["substation"],
  transco: ["transco", "substation"],
  discom: ["discom", "substation"],
  circle: ["circle"],
  plant: ["plant", "generation"],
  solar: ["solar", "plant"],
  wind: ["wind", "plant"],
  thermal: ["thermal", "plant"],
  load: ["load", "demand", "consumption"],
  peak_load: ["peak load", "peak demand"],
  generation: ["generation"],
  net_load: ["net load"],
  net_consumption: ["net consumption"],
  forecast: ["forecast"],
  weather: ["weather", "temperature", "humidity", "wind", "rain"],
  temperature: ["temperature"],
  humidity: ["humidity"],
  rain: ["rain", "precipitation"],
  wind_weather: ["wind"],
  cloud: ["cloud"],
  sunshine: ["sunshine", "sun"],
  radiation: ["radiation"],
  forecast_adjustment: ["forecast"],
  load_map: ["load map", "map"],
  data_quality: ["data quality", "quality"],
  ranking: ["ranking", "top", "bottom"],
};

/**
 * Normalize free-text search before matching: lowercase, fold separators and
 * dots, collapse whitespace, then canonicalize a few domain spellings/units so
 * alias lookups land on a single key (e.g. "132 kv" → "132kv", "G.S.S" → "gss",
 * "sub station" → "substation", "forcast" → "forecast").
 */
export function normalizeSearchText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[-_]/g, " ")
    .replace(/\./g, "")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b132\s+kv\b/g, "132kv")
    .replace(/\b220\s+kv\b/g, "220kv")
    .replace(/\b400\s+kv\b/g, "400kv")
    .replace(/\bg\s*\.?\s*s\s*\.?\s*s\b/g, "gss")
    .replace(/\bsub\s+station\b/g, "substation")
    .replace(/\bforcast\b/g, "forecast");
}

// Reverse index: normalized alias phrase → set of canonical terms to match.
// Built once at module load. Multiple categories can share an alias (e.g.
// "gss" → transco + substation), so terms accumulate across categories.
const ALIAS_TO_TERMS: Map<string, string[]> = (() => {
  const map = new Map<string, Set<string>>();
  for (const [key, aliases] of Object.entries(SEARCH_ALIASES)) {
    const terms = CANONICAL_TERMS[key] ?? [key.replace(/_/g, " ")];
    for (const alias of aliases) {
      const norm = normalizeSearchText(alias);
      if (!norm) continue;
      const set = map.get(norm) ?? new Set<string>();
      terms.forEach((t) => set.add(t));
      map.set(norm, set);
    }
  }
  const out = new Map<string, string[]>();
  for (const [k, v] of map) out.set(k, Array.from(v));
  return out;
})();

// Per-query expansion cache (alias lookup is pure → safe to memoize for the
// session). Keeps alias resolution off the hot path on repeat keystrokes.
const aliasCache = new Map<string, string[]>();

/**
 * Expand a normalized query into the extra canonical terms its aliases map to.
 * Returns [] when nothing matches or the query is too short (<3 chars) to avoid
 * noise. A query matches an alias when it EQUALS the alias or the alias starts
 * with it (so "subst" already reaches "substation"'s canonical terms).
 */
export function expandAliases(normalizedQuery: string): string[] {
  if (normalizedQuery.length < 3) return [];
  const cached = aliasCache.get(normalizedQuery);
  if (cached) return cached;

  const terms = new Set<string>();
  // Exact alias hit (cheap path) — and prefix hits so partially-typed aliases
  // still expand. Bounded by the static alias table, so cost is constant.
  for (const [alias, canon] of ALIAS_TO_TERMS) {
    if (alias === normalizedQuery || alias.startsWith(normalizedQuery)) {
      canon.forEach((t) => terms.add(t));
    }
  }
  const out = Array.from(terms);
  aliasCache.set(normalizedQuery, out);
  return out;
}
